import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import axios from "../axios";
import {
  API_Q_MEDALS,
  API_Q_MEDALS_LIST,
  API_Q_CREATE_MEDALS,
  API_UPDATE_AVATAR,
  API_FREE_CAN_PLAY,
  API_Q_GOALS,
  API_POST_ADD_COIN,
} from "../constants/api";
import SystemContext from "@context/SystemContext";

export const USER_KEYS = {
  byUser: (userId) => ["user", userId],
  goal: (userId) => ["goal", userId],
  medalsCatalog: ["medals", "catalog"],
};

// Requisição assíncrona isolada recebendo currentUser via parâmetro
async function requestUserInfos(currentUser, ignoreMedals = false) {
  try {
    if (currentUser) {
      const [medalsResponse, freeCanPlayResponse] = await Promise.all([
        axios.get(`${API_Q_MEDALS}/${currentUser?.sub}`, {
          params: { ignoreMedals },
        }),
        axios.get(API_FREE_CAN_PLAY + "?read=only"),
      ]);

      return { ...medalsResponse.data, freeCanPlay: freeCanPlayResponse.data };
    }


    const response = await axios.get(API_Q_MEDALS_LIST, {
      forceWithoutAuth: true,
    });

    return { medals: response.data };
  } catch (error) {
    throw error;
  }
}

async function requestUserGoal(userId) {
  try {
    if (!userId) {
      throw new Error("requestUserGoal: userId não informado");
    }

    const response = await axios.get(`${API_Q_GOALS}/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function requestClaimUserGoal({ userSub, conquest }) {
  const response = await axios.post(API_POST_ADD_COIN, {
    user_sub: userSub,
    conquest,
  });

  return response.data;
}

async function requestPublicMedalsCatalog() {
  const response = await axios.get(API_Q_MEDALS_LIST, {
    forceWithoutAuth: true,
  });

  return Array.isArray(response.data)
    ? { medals: response.data }
    : response.data;
}

async function requestCreateUserMedals(userSub) {
  if (!userSub) {
    throw new Error("requestCreateUserMedals: userSub não informado");
  }

  const response = await axios.post(`${API_Q_CREATE_MEDALS}/${userSub}`, {});
  return response.data;
}

async function updatePlaysFree(openGameIsQuizRef) {
  try {
    const response = await axios.put(API_FREE_CAN_PLAY + openGameIsQuizRef);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export function useUpdateUserPlaysFree() {
  const queryClient = useQueryClient();
  const { currentUser } = useContext(SystemContext);

  const { mutate, isLoading } = useMutation({
    mutationFn: (openGameIsQuizRef) => updatePlaysFree(openGameIsQuizRef),

    onSuccess: (data, variables) => {

      queryClient.setQueryData(USER_KEYS.byUser(currentUser.sub), (oldData) => {
        if (!oldData) return oldData;

        // Preserva o objeto inteiro do usuário (...oldData) 
        // e atualiza apenas a propriedade freeCanPlay
        const newData = {
          ...oldData,
          freeCanPlay: {
            ...oldData.freeCanPlay,
            ...(data?.freeCanPlay || data), // mescla a resposta da API em freeCanPlay
          },
        };
        return newData;
      });
    },
  });

  return { mutate, isLoading };
}

// Hook para obter informações da conta do usuário ativo com dados mesclados
export function useUserAccountInfos(customSelect) {
  const { currentUser, isLoadingUser } = useContext(SystemContext); // Adicionado tratamento de loading do contexto se houver
  const query = useQuery({
    queryKey: USER_KEYS.byUser(currentUser?.sub),
    queryFn: () => requestUserInfos(currentUser),
    // Só ativa a query se existir o ID válido do usuário
    enabled: Boolean(currentUser?.sub),
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    meta: { persist: true },
  });

  // Aplica a mescla PRIMEIRO sobre o objeto completo retornado da API
  const mergedData = useMemo(() => {
    // Se ainda está carregando o contexto ou não há usuário, não tenta mesclar
    if (!currentUser) return query.data;
    if (!query.data || typeof query.data !== "object") return currentUser;

    const baseMerged = {
      ...currentUser,
      ...query.data,
      my_user: {
        ...(currentUser.my_user ?? {}),
        ...(query.data?.my_user ?? {}),
      },
    };

    // Aplica o customSelect na estrutura JÁ MESCLADA (se fornecido)
    return customSelect ? customSelect(baseMerged) : baseMerged;
  }, [currentUser, query.data, customSelect]);

  return {
    ...query,
    data: mergedData,
    // Flag utilitária para saber se a montagem inicial ainda está pendente
    isInitializing: Boolean(isLoadingUser) || (!currentUser && query.isLoading),
  };
}

export function useUserGoal({ enabled = true } = {}) {
  const { currentUser } = useContext(SystemContext);

  return useQuery({
    queryKey: USER_KEYS.goal(currentUser?.sub),
    queryFn: () => requestUserGoal(currentUser?.sub),
    enabled: Boolean(currentUser?.sub) && enabled,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useClaimUserGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestClaimUserGoal,
    retry: false,
    onSuccess: (data, variables) => {
      if (!data?.user_sub) return;

      queryClient.setQueryData(USER_KEYS.byUser(data.user_sub), (oldData) => {
        if (!oldData || data?.star == null) return oldData;

        return {
          ...oldData,
          stars: String(data.star),
        };
      });

      const conquest = variables?.conquest;

      if (conquest) {
        queryClient.setQueryData(USER_KEYS.goal(data.user_sub), (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            [`amount_played_${conquest}_status`]: "claimed",
          };
        });
      }

      queryClient.invalidateQueries({
        queryKey: USER_KEYS.goal(data.user_sub),
        refetchType: "none",
      });
    },
  });
}

export function usePublicMedalsCatalog({ enabled = true } = {}) {
  return useQuery({
    queryKey: USER_KEYS.medalsCatalog,
    queryFn: requestPublicMedalsCatalog,
    enabled,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useCreateUserMedals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestCreateUserMedals,
    retry: false,
    onSuccess: (_, userSub) => {
      queryClient.invalidateQueries({
        queryKey: USER_KEYS.byUser(userSub),
      });
    },
  });
}

// Hook para buscar uma medalha específica por ID
export function useUserMedalById(idMedal) {
  const { currentUser } = useContext(SystemContext);

  return useQuery({
    queryKey: USER_KEYS.byUser(currentUser?.sub),
    queryFn: () => requestUserInfos(currentUser),
    enabled: !!currentUser?.sub && !!idMedal,
    staleTime: 1000 * 60 * 5,
    select: (data) =>
      data?.medals?.find((medal) => medal.game_id?.[0] == idMedal),
  });
}

// Custom Hook para atualizar moedas diretamente no cache
export function useUpdateUserCoins() {
  const queryClient = useQueryClient();
  const { currentUser } = useContext(SystemContext);

  // deltaStars: passe valor positivo para somar (+10) ou negativo para subtrair (-5)
  return (deltaStars) => {
    if (!currentUser?.sub) return;

    queryClient.setQueryData(USER_KEYS.byUser(currentUser.sub), (oldData) => {
      if (!oldData) return oldData;

      const currentStars = parseInt(oldData?.stars, 10) || 0;
      const amount = parseInt(deltaStars, 10) || 0;

      // Soma o delta e garante que as moedas não fiquem abaixo de 0
      const updatedStars = Math.max(0, currentStars + amount).toString();

      return {
        ...oldData,
        stars: updatedStars,
      };
    });
  };
}

// Custom Hook para invalidar a cache do usuário ativo
export function useInvalidateUserCache() {
  const queryClient = useQueryClient();
  const { currentUser } = useContext(SystemContext);

  return ({ includeGoal = false } = {}) => {
    if (!currentUser?.sub) return;

    queryClient.invalidateQueries({
      queryKey: USER_KEYS.byUser(currentUser.sub),
    });

    if (includeGoal) {
      queryClient.invalidateQueries({
        queryKey: USER_KEYS.goal(currentUser.sub),
        refetchType: "none",
      });
    }
  };
}

// Custom Hook para atualizar as tags
export function useUpdateUserTags() {
  const queryClient = useQueryClient();
  const { currentUser } = useContext(SystemContext);

  return (newTags) => {
    if (!currentUser?.sub) return;

    queryClient.setQueryData(USER_KEYS.byUser(currentUser.sub), (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        tags: newTags,
      };
    });
  };
}

//Envie uma alteração de avatar para o backend.
async function requestUserAvatar({ userSub, rewardId }) {
  try {
    const response = await axios({
      url: API_UPDATE_AVATAR,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      data: { user_sub: userSub, reward_id: rewardId },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
}

export function useUpdateUserAvatar() {
  // Acessa o cache do TanStack
  const queryClient = useQueryClient();

  // Pega o usuário atual
  const { currentUser } = useContext(SystemContext);
  const { mutate, isLoading } = useMutation({
    // Faz o PUT para atualizar o avatar
    mutationFn: ({ rewardId }) =>
      requestUserAvatar({
        userSub: currentUser?.sub,
        rewardId,
      }),

    // Depois que o PUT deu certo
    onSuccess: (_, variables) => {
      // Atualiza apenas o avatar do usuário no cache
      queryClient.setQueryData(USER_KEYS.byUser(currentUser.sub), (oldData) => {
        if (!oldData) return oldData;

        const newData = {
          ...oldData,
          // Pega o avatar que foi passado no mutate
          avatar: variables.avatar,
        };
        return newData;
      });
    },
  });
  return { mutate, isLoading };
}
